import React, { createContext, useContext, useState, useMemo } from "react";
import { useResponsive } from "./ResponsiveContext";

interface SearchContextType {
  searchParams: SearchParams;
  updateSearchParams: (params: Partial<SearchParams>) => void;
  clearSearch: () => void;
  isCompactView: boolean;
  toggleView: () => void;
  // Add these new properties
  origin?: string;
  destination?: string;
}

interface SearchParams {
  from: string;
  to: string;
  departureDate: string;
  returnDate?: string;
  passengers: {
    ADT: number; // changed from adults to ADT
    CHD: number; // changed from children to CHD
    INF: number; // changed from infants to INF
  };
  cabinClass: string;
  directFlights: boolean;
}

const SearchContext = createContext<SearchContextType>({} as SearchContextType);

export const SearchProvider: React.FC<{
  children: React.ReactNode;
  initial?: Partial<SearchParams>;
}> = ({ children, initial }) => {
  const { isMobile } = useResponsive();
  const [isCompactView, setIsCompactView] = useState(isMobile);

  const [searchParams, setSearchParams] = useState<SearchParams>({
    from: "",
    to: "",
    departureDate: "",
    passengers: {
      ADT: 1, // changed from adults to ADT
      CHD: 0, // changed from children to CHD
      INF: 0, // changed from infants to INF
    },
    cabinClass: "Economy",
    directFlights: false,
  });

  const updateSearchParams = (params: Partial<SearchParams>) => {
    setSearchParams((prev) => ({ ...prev, ...params }));
  };

  const clearSearch = () => {
    setSearchParams({
      from: "",
      to: "",
      departureDate: "",
      passengers: {
        ADT: 1, // changed from adults to ADT
        CHD: 0, // changed from children to CHD
        INF: 0, // changed from infants to INF
      },
      cabinClass: "Economy",
      directFlights: false,
    });
  };

  const toggleView = () => setIsCompactView((prev) => !prev);

  const value = useMemo(
    () => ({
      searchParams,
      updateSearchParams,
      clearSearch,
      isCompactView,
      toggleView,
      // Add these computed properties
      origin: searchParams.from,
      destination: searchParams.to,
    }),
    [searchParams, isCompactView]
  );

  //   useEffect(() => {
  //     console.log("SearchContext values:", { origin, destination });
  //   }, [origin, destination]);

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export const useSearch = () => useContext(SearchContext);
